import { useCallback, useEffect, useMemo, useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useApiFetch } from '@/hooks/useApiFetch'
import { useApiMutation } from '@/hooks/useApiMutation'
import { useAdminFilters } from '@/hooks/useAdminFilters'
import { formatDate, formatNumber } from '@/ui/format'
import { StatusBadge } from '@/components/StatusBadge'
import { Alert, Badge, Button, DatePicker, Input, Modal, Pagination, Select, StatCard, Table, ViewReasonModal } from '@/ui'
import type { TableColumn } from '@/ui'
import styles from './AIGallery.module.css'

// --- 타입 ---

interface GalleryUser {
  email: string
  name: string | null
}

// 리스트 응답: 사진 URL·프롬프트 본문 미포함 (개인정보 최소화). 개수·메타만.
interface GalleryActivity {
  id: string
  userId: string
  type: 'generation' | 'batch_job' | 'theme'
  status: string
  modelId: string | null
  style: string | null
  role: string | null
  creditsUsed: number | null
  cost: number | null
  createdAt: string
  completedAt: string | null
  originalUrlCount: number
  generatedUrlCount: number
  isAdminTest: boolean
  user: GalleryUser
}

interface GalleryResponse {
  activities: GalleryActivity[]
  pagination: { page: number; pageSize: number; total: number; totalPages: number }
  stats: { totalCount: number; totalCost: number; avgCost: number; failRate: number }
}

// 열람 응답: 사유 입력 후에만 URL·프롬프트 반환
interface ActivityViewResult {
  originalUrl: string | null
  originalUrls: string[] | null
  generatedUrls: string[] | null
  thumbUrls: string[] | null
  customPrompt: string | null
}

// --- 상수 ---

const STATUS_OPTIONS = [
  { value: 'completed', label: '성공만' },
  { value: '', label: '전체' },
  { value: 'partial', label: 'partial' },
  { value: 'failed', label: 'failed' },
]

const STYLE_OPTIONS = [
  { value: '', label: '전체 스타일' },
  { value: 'CLASSIC_STUDIO', label: '클래식 스튜디오' },
  { value: 'OUTDOOR_GARDEN', label: '아웃도어 가든' },
  { value: 'SUNSET_BEACH', label: '선셋 비치' },
  { value: 'TRADITIONAL_HANBOK', label: '전통 한복' },
  { value: 'VINTAGE_CINEMATIC', label: '빈티지 시네마' },
  { value: 'LUXURY_HOTEL', label: '럭셔리 호텔' },
  { value: 'CITY_LIFESTYLE', label: '시티 라이프' },
  { value: 'ENCHANTED_FOREST', label: '숲속' },
  { value: 'BLACK_AND_WHITE', label: '흑백' },
  { value: 'MINIMALIST_GALLERY', label: '미니멀 갤러리' },
]

const MODEL_OPTIONS = [
  { value: '', label: '전체 모델' },
  { value: 'gpt-image-1', label: 'GPT Image 1' },
  { value: 'gpt-image-1.5', label: 'GPT Image 1.5' },
  { value: 'dall-e-3', label: 'DALL-E 3' },
  { value: 'gemini-flash-image', label: 'Gemini Flash' },
  { value: 'gemini-3-pro-image', label: 'Gemini 3 Pro' },
]

const ADMIN_TEST_OPTIONS = [
  { value: 'false', label: '일반만' },
  { value: '', label: '전체' },
  { value: 'true', label: '테스트만' },
]

// --- 컴포넌트 ---

export function AIGallery() {
  const { filters, setFilters, resetFilters } = useAdminFilters({ pageSize: 30 })

  const [statusFilter, setStatusFilter] = useState('completed')
  const [styleFilter, setStyleFilter] = useState('')
  const [modelFilter, setModelFilter] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [searchDebounced, setSearchDebounced] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [adminTestFilter, setAdminTestFilter] = useState('false')

  // 열람 사유 입력 대상 (활동)
  const [reasonTarget, setReasonTarget] = useState<{ id: string; label: string } | null>(null)
  const [lightbox, setLightbox] = useState<{ urls: string[]; index: number; label: string } | null>(null)

  const viewMutation = useApiMutation<ActivityViewResult, { reason: string }>(
    '/api/admin/ai-activity-history/_/view',
  )

  useEffect(() => {
    resetFilters()
  }, [resetFilters])

  // 300ms debounce
  useEffect(() => {
    const t = setTimeout(() => {
      setSearchDebounced(searchInput)
      setFilters({ page: 1 })
    }, 300)
    return () => clearTimeout(t)
  }, [searchInput, setFilters])

  const params = useMemo(() => {
    const p = new URLSearchParams()
    p.set('page', String(filters.page))
    p.set('pageSize', String(filters.pageSize))
    p.set('sortBy', filters.sortBy)
    p.set('sortOrder', filters.sortOrder)
    // 갤러리는 항상 generation 타입만 (이미지 생성 결과만)
    p.set('type', 'generation')
    if (statusFilter) p.set('status', statusFilter)
    if (styleFilter) p.set('style', styleFilter)
    if (modelFilter) p.set('modelId', modelFilter)
    if (searchDebounced) p.set('search', searchDebounced)
    if (startDate) p.set('startDate', new Date(startDate).toISOString())
    if (endDate) p.set('endDate', new Date(endDate + 'T23:59:59').toISOString())
    if (adminTestFilter) p.set('isAdminTest', adminTestFilter)
    return p.toString()
  }, [filters, statusFilter, styleFilter, modelFilter, searchDebounced, startDate, endDate, adminTestFilter])

  const { data, isLoading, error } = useApiFetch<GalleryResponse>(
    `/api/admin/ai-activity-history?${params}`,
  )

  const handleResetFilters = useCallback(() => {
    setStatusFilter('completed')
    setStyleFilter('')
    setModelFilter('')
    setSearchInput('')
    setSearchDebounced('')
    setStartDate('')
    setEndDate('')
    setAdminTestFilter('false')
    resetFilters()
  }, [resetFilters])

  // 이미지 있는 활동만 (status=completed면 자연히 보장되지만, 다른 status 보호)
  const activities = useMemo(
    () => (data?.activities ?? []).filter(a => a.generatedUrlCount > 0),
    [data],
  )

  // 라이트박스 키보드 네비
  useEffect(() => {
    if (!lightbox) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'ArrowRight') setLightbox(prev => prev ? { ...prev, index: (prev.index + 1) % prev.urls.length } : prev)
      else if (e.key === 'ArrowLeft') setLightbox(prev => prev ? { ...prev, index: (prev.index - 1 + prev.urls.length) % prev.urls.length } : prev)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [lightbox])

  const openReason = (a: GalleryActivity) => {
    viewMutation.reset()
    setReasonTarget({ id: a.id, label: a.user.name ?? a.user.email ?? '익명' })
  }

  const handleConfirmView = async (reason: string) => {
    if (!reasonTarget) return
    const res = await viewMutation.trigger(
      { reason },
      { pathOverride: `/api/admin/ai-activity-history/${reasonTarget.id}/view` },
    )
    if (res) {
      const urls =
        res.generatedUrls && res.generatedUrls.length > 0
          ? res.generatedUrls
          : res.originalUrls ?? (res.originalUrl ? [res.originalUrl] : [])
      if (urls.length > 0) {
        setLightbox({ urls, index: 0, label: reasonTarget.label })
      }
      setReasonTarget(null)
      viewMutation.reset()
    }
  }

  const columns: TableColumn<GalleryActivity>[] = [
    {
      key: 'user',
      label: '유저',
      render: (a) => (
        <div className={styles.userCell}>
          <span className={styles.userName}>{a.user.name ?? a.user.email ?? '익명'}</span>
          {a.isAdminTest && <Badge color="yellow">테스트</Badge>}
        </div>
      ),
    },
    { key: 'status', label: '상태', width: '90px', render: (a) => <StatusBadge status={a.status} /> },
    { key: 'modelId', label: '모델', width: '140px', render: (a) => a.modelId ?? '-' },
    { key: 'style', label: '스타일', width: '140px', render: (a) => a.style ?? '-' },
    {
      key: 'generated',
      label: '생성',
      width: '110px',
      align: 'right',
      render: (a) => (
        <span>
          {a.generatedUrlCount}장
          {a.creditsUsed != null && <span className={styles.metaDim}> · {a.creditsUsed}C</span>}
        </span>
      ),
    },
    { key: 'createdAt', label: '일시', width: '150px', render: (a) => formatDate(a.createdAt, 'yyyy.M.d HH:mm') },
    {
      key: 'action',
      label: '',
      width: '80px',
      align: 'right',
      render: (a) => (
        <Button size="sm" variant="outline" onClick={() => openReason(a)}>
          보기
        </Button>
      ),
    },
  ]

  return (
    <div className={styles.root}>
      {/* 통계 카드 */}
      <div className={styles.statGrid}>
        <StatCard label="총 활동 수" value={formatNumber(data?.stats.totalCount ?? 0)} />
        <StatCard label="페이지 활동" value={formatNumber(data?.pagination.total ?? 0)} />
        <StatCard label="총 비용" value={`$${(data?.stats.totalCost ?? 0).toFixed(2)}`} />
        <StatCard label="평균 비용" value={`$${(data?.stats.avgCost ?? 0).toFixed(4)}`} />
      </div>

      {/* 필터 */}
      <div className={styles.filterBar}>
        <Input
          size="sm"
          placeholder="유저 이메일/이름 검색"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className={styles.search}
        />
        <Select size="sm" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setFilters({ page: 1 }) }} options={STATUS_OPTIONS} />
        <Select size="sm" value={styleFilter} onChange={(e) => { setStyleFilter(e.target.value); setFilters({ page: 1 }) }} options={STYLE_OPTIONS} />
        <Select size="sm" value={modelFilter} onChange={(e) => { setModelFilter(e.target.value); setFilters({ page: 1 }) }} options={MODEL_OPTIONS} />
        <DatePicker
          size="sm"
          value={startDate}
          onChange={(_d: Date | null, formatted: string) => { setStartDate(formatted); setFilters({ page: 1 }) }}
          placeholder="시작일"
        />
        <DatePicker
          size="sm"
          value={endDate}
          onChange={(_d: Date | null, formatted: string) => { setEndDate(formatted); setFilters({ page: 1 }) }}
          placeholder="종료일"
        />
        <Select size="sm" value={adminTestFilter} onChange={(e) => { setAdminTestFilter(e.target.value); setFilters({ page: 1 }) }} options={ADMIN_TEST_OPTIONS} />
        <Button size="sm" variant="ghost" onClick={handleResetFilters}>초기화</Button>
      </div>

      {/* 에러 */}
      {error && <Alert variant="danger">{error}</Alert>}

      {/* 활동 리스트 (이미지는 [보기]+사유 입력 후에만 표시) */}
      <Table
        columns={columns}
        data={activities}
        rowKey={(a) => a.id}
        isLoading={isLoading && !data}
        emptyMessage="조건에 맞는 생성 결과가 없습니다."
      />

      {/* 페이지네이션 */}
      {data && data.pagination.totalPages > 1 && (
        <div className={styles.paginationWrap}>
          <Pagination
            current={data.pagination.page}
            total={data.pagination.totalPages}
            onChange={(page) => setFilters({ page })}
          />
        </div>
      )}

      {/* 열람 사유 입력 */}
      <ViewReasonModal
        open={!!reasonTarget}
        onClose={() => { setReasonTarget(null); viewMutation.reset() }}
        onConfirm={handleConfirmView}
        title={reasonTarget ? `${reasonTarget.label} 생성 이미지 열람` : '열람 사유 입력'}
        description="생성 이미지·참조 사진 열람은 접속기록으로 남습니다. 열람 사유를 입력해 주세요."
        loading={viewMutation.isLoading}
        error={viewMutation.error}
      />

      {/* 라이트박스 */}
      <Modal
        open={!!lightbox}
        onClose={() => setLightbox(null)}
        size="xl"
        title={lightbox ? `${lightbox.label} (${lightbox.index + 1}/${lightbox.urls.length})` : undefined}
      >
        {lightbox && (
          <div className={styles.lightbox}>
            <button
              type="button"
              className={styles.lightboxNav}
              onClick={() => setLightbox({ ...lightbox, index: (lightbox.index - 1 + lightbox.urls.length) % lightbox.urls.length })}
              aria-label="이전"
              disabled={lightbox.urls.length < 2}
            >
              <ChevronLeft size={24} />
            </button>
            <img src={lightbox.urls[lightbox.index]} alt={`${lightbox.label} ${lightbox.index + 1}`} className={styles.lightboxImg} />
            <button
              type="button"
              className={styles.lightboxNav}
              onClick={() => setLightbox({ ...lightbox, index: (lightbox.index + 1) % lightbox.urls.length })}
              aria-label="다음"
              disabled={lightbox.urls.length < 2}
            >
              <ChevronRight size={24} />
            </button>
          </div>
        )}
        {lightbox && (
          <div className={styles.lightboxFooter}>
            <a href={lightbox.urls[lightbox.index]} target="_blank" rel="noopener noreferrer" className={styles.lightboxLink}>
              원본 열기
            </a>
          </div>
        )}
      </Modal>
    </div>
  )
}
